import React, {createContext, useContext, useEffect, useMemo} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {isSectionPanelsAuto} from '../../../state/views/compress';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {
  hideSearchRegexMatchesPanel,
  searchRegexMatchesPanel,
} from '../../../util/panelbank';
import {type LayedOutPanelWithRef} from '../../../util/panelTypes';
import {usePanelIsActive} from '../../../util/usePanelIsActive';
import {useWorkspaceLayoutSettings} from '../../WorkspaceDrawer/Settings/hooks';
import {usePanelBankConfigWithRefs} from '../hooks/usePanelBankConfigWithRefs';
import {getIsSectionNameSearchMatch} from '../sectionLayout/useSectionNameMatch';
import {getPanelBankSectionsForDisplay} from '../sections/getPanelBankSectionsForDisplay';
import {type SectionWithRef} from '../types';

export type PanelBankSectionSearchResult = {
  activePanels: LayedOutPanelWithRef[];
  filteredPanels: LayedOutPanelWithRef[];
  isSectionNameSearchMatch: boolean;
  panelSearchMatchCount: number;
  section: SectionWithRef;
};

type PanelBankSearchResultsContextValue = {
  visibleSections: SectionWithRef[];
  sectionResultsById: ReadonlyMap<string, PanelBankSectionSearchResult>;
  numCollapsedSections: number;
  numExpandedSections: number;
  numPanelsMatched: number;
  numSectionsMatched: number;
  numPanelsHidden: number;
};

const EMPTY_SEARCH_RESULTS: PanelBankSearchResultsContextValue = {
  visibleSections: [],
  sectionResultsById: new Map(),
  numCollapsedSections: 0,
  numExpandedSections: 0,
  numPanelsMatched: 0,
  numSectionsMatched: 0,
  numPanelsHidden: 0,
};

const PanelBankSearchResultsContext = createContext<
  PanelBankSearchResultsContextValue | undefined
>(undefined);

const getSectionSearchResultId = (section: SectionWithRef) => section.ref.id;

export const PanelBankSearchResultsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {sections: panelBankSections} = usePanelBankConfigWithRefs();
  const workspaceLayout = useWorkspaceLayoutSettings();
  const panelIsActive = usePanelIsActive();
  const {
    debouncedHideSearchRegex: hideSearchRegex,
    debouncedSearchRegex: searchRegex,
    isHiding,
    isSearching,
  } = usePanelSearchDebouncedContext();

  const allSections = useMemo(() => {
    return getPanelBankSectionsForDisplay(panelBankSections);
  }, [panelBankSections]);

  const value = useMemo(() => {
    if (allSections.length === 0) {
      return EMPTY_SEARCH_RESULTS;
    }

    const hasPanelSearchFilter = isSearching || isHiding;
    const shouldShowEmptySectionsDuringSearch =
      workspaceLayout.showEmptySections && hasPanelSearchFilter;
    const visibleSections: SectionWithRef[] = [];
    const sectionResultsById = new Map<string, PanelBankSectionSearchResult>();
    let numCollapsedSections = 0;
    let numExpandedSections = 0;
    let numPanelsMatched = 0;
    let numSectionsMatched = 0;
    let numPanelsHidden = 0;

    for (const section of allSections) {
      const isUserCreatedEmptySection =
        section.panels.length === 0 &&
        !isSectionPanelsAuto(section, workspaceLayout);

      const activePanels: LayedOutPanelWithRef[] = [];
      const panelSearchMatchedPanels: LayedOutPanelWithRef[] = [];
      const isSectionNameSearchMatch =
        searchRegex != null &&
        getIsSectionNameSearchMatch(section.name, searchRegex);
      let panelSearchMatchCount = 0;
      let hasPanelSearchMatch = false;
      let hasPanelSearchMatchBeforeHide = false;
      let hiddenPanelSearchMatchCount = 0;
      let hiddenPanelSectionNameOnlyCount = 0;

      for (const panel of section.panels) {
        const isActive = panelIsActive({section, panel});

        if (!isActive) {
          continue;
        }

        const isPanelSearchMatch =
          searchRegex == null || searchRegexMatchesPanel(searchRegex, panel);
        if (searchRegex != null && isPanelSearchMatch) {
          hasPanelSearchMatchBeforeHide = true;
        }

        const isHidden =
          hideSearchRegex != null &&
          hideSearchRegexMatchesPanel(hideSearchRegex, panel);
        if (isHidden) {
          if (searchRegex == null || isPanelSearchMatch) {
            hiddenPanelSearchMatchCount++;
          } else if (isSectionNameSearchMatch) {
            hiddenPanelSectionNameOnlyCount++;
          }
          continue;
        }

        activePanels.push(panel);

        if (searchRegex == null) {
          continue;
        }

        if (isPanelSearchMatch) {
          hasPanelSearchMatch = true;

          if (
            !isSectionNameSearchMatch ||
            shouldShowEmptySectionsDuringSearch
          ) {
            panelSearchMatchedPanels.push(panel);
          }

          panelSearchMatchCount++;
        }
      }

      numPanelsHidden +=
        hiddenPanelSearchMatchCount +
        (isSectionNameSearchMatch && hasPanelSearchMatchBeforeHide
          ? hiddenPanelSectionNameOnlyCount
          : 0);

      const isVisible =
        shouldShowEmptySectionsDuringSearch ||
        isUserCreatedEmptySection ||
        (searchRegex == null ? activePanels.length > 0 : hasPanelSearchMatch);

      if (!isVisible) {
        continue;
      }

      const filteredPanels =
        searchRegex == null || isSectionNameSearchMatch
          ? activePanels
          : panelSearchMatchedPanels;
      const displayedPanelSearchMatchCount = filteredPanels.length;

      visibleSections.push(section);
      if (section.isOpen) {
        numExpandedSections++;
      } else {
        numCollapsedSections++;
      }
      sectionResultsById.set(getSectionSearchResultId(section), {
        activePanels,
        filteredPanels,
        isSectionNameSearchMatch,
        panelSearchMatchCount: displayedPanelSearchMatchCount,
        section,
      });

      if (searchRegex != null && isSectionNameSearchMatch) {
        numSectionsMatched++;
        numPanelsMatched += activePanels.length;
      } else if (searchRegex != null) {
        numPanelsMatched += panelSearchMatchCount;
      } else if (hideSearchRegex != null) {
        numPanelsMatched += activePanels.length;
      }
    }

    return {
      visibleSections,
      sectionResultsById,
      numCollapsedSections,
      numExpandedSections,
      numPanelsMatched,
      numSectionsMatched,
      numPanelsHidden,
    };
  }, [
    allSections,
    hideSearchRegex,
    isHiding,
    isSearching,
    panelIsActive,
    searchRegex,
    workspaceLayout,
  ]);

  const {
    numCollapsedSections,
    numExpandedSections,
    numPanelsHidden,
    numPanelsMatched,
    numSectionsMatched,
    visibleSections,
  } = value;
  const numVisibleSections = visibleSections.length;
  useEffect(() => {
    recordRumAction('workspace.num_search_matches', {
      workspaceSearch: {
        isHiding,
        isSearching,
        numCollapsedSections,
        numExpandedSections,
        numPanelsHidden,
        numPanelsMatched,
        numSectionsMatched,
        numVisibleSections,
      },
      workspaceLayout,
    });
  }, [
    isHiding,
    isSearching,
    numCollapsedSections,
    numExpandedSections,
    numPanelsHidden,
    numPanelsMatched,
    numSectionsMatched,
    numVisibleSections,
    workspaceLayout,
  ]);

  return (
    <PanelBankSearchResultsContext.Provider value={value}>
      {children}
    </PanelBankSearchResultsContext.Provider>
  );
};

export function usePanelBankSearchResults() {
  const context = useContext(PanelBankSearchResultsContext);
  if (context == null) {
    throw new Error(
      'usePanelBankSearchResults must be used within a PanelBankSearchResultsProvider'
    );
  }

  return context;
}

export function usePanelBankSectionSearchResult(section: SectionWithRef) {
  const {sectionResultsById} = usePanelBankSearchResults();
  return sectionResultsById.get(getSectionSearchResultId(section));
}
